import { useState, useCallback } from 'react';
import { getNestedValue } from '../utils/validationUtils';

/**
 * Custom hook for managing form state with validation
 * 
 * This hook provides a standardized way to manage form state,
 * handle field changes, and track validation errors across forms.
 * 
 * @param {Object} initialValues - Initial form values
 * @returns {Object} Form state and handlers
 */
const useForm = (initialValues = {}) => {
  // Form data state
  const [formData, setFormData] = useState(initialValues);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  /**
   * Updates form data with new values
   * @param {Object} newValues - New values to merge with current form data
   */
  const updateFormData = useCallback((newValues) => {
    setFormData(prev => ({
      ...prev,
      ...newValues
    }));
  }, []);

  /**
   * Handles simple field change
   * @param {string} fieldName - Name of the field to update
   * @param {*} value - New value for the field
   */
  const handleChange = useCallback((fieldName, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
    
    // Clear validation error if it exists and value is present
    if (validationErrors[fieldName] && value) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[fieldName];
        return updated;
      });
    }
  }, [validationErrors]);

  /**
   * Handles nested field change (object properties)
   * @param {string} parent - Parent object name
   * @param {string} field - Field name within parent object
   * @param {*} value - New value for the field
   */
  const handleNestedChange = useCallback((parent, field, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent] || {}),
        [field]: value
      }
    }));
    
    // Mark nested field as touched
    setTouchedFields(prev => ({
      ...prev,
      [`${parent}.${field}`]: true
    }));
    
    // Clear validation error if it exists and value is present
    const nestedFieldKey = `${parent}.${field}`;
    if (validationErrors[nestedFieldKey] && value) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[nestedFieldKey];
        return updated;
      });
    }
  }, [validationErrors]);

  /**
   * Handles deeply nested field change (3 levels)
   * @param {string} parent - Top level object name
   * @param {string} child - Second level object name
   * @param {string} field - Field name within child object
   * @param {*} value - New value for the field
   */
  const handleDeepNestedChange = useCallback((parent, child, field, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent] || {}),
        [child]: {
          ...((prev[parent] || {})[child] || {}),
          [field]: value
        }
      }
    }));
    
    // Mark deeply nested field as touched
    setTouchedFields(prev => ({
      ...prev,
      [`${parent}.${child}.${field}`]: true
    }));
    
    // Clear validation error if it exists and value is present
    const deepNestedFieldKey = `${parent}.${child}.${field}`;
    if (validationErrors[deepNestedFieldKey] && value) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[deepNestedFieldKey];
        return updated;
      });
    }
  }, [validationErrors]);

  /**
   * Handles array field changes (adding, removing, updating items)
   * @param {string} fieldName - Name of the array field
   * @param {Array} newArray - New array value
   */
  const handleArrayChange = useCallback((fieldName, newArray) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fieldName]: newArray
    }));
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
    
    // Clear validation error if array is not empty
    if (validationErrors[fieldName] && newArray && newArray.length > 0) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[updated];
        return updated;
      });
    }
  }, [validationErrors]);

  /**
   * Validates a specific field
   * @param {string} fieldPath - Path to the field (supports dot notation)
   * @param {Function} validator - Validation function returning true/false
   * @param {string} errorMessage - Error message if validation fails
   * @returns {boolean} Validation result
   */
  const validateField = useCallback((fieldPath, validator, errorMessage) => {
    const value = getNestedValue(formData, fieldPath);
    const isValid = validator(value, formData);
    
    if (!isValid) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldPath]: errorMessage || 'Invalid value'
      }));
      
      setTouchedFields(prev => ({
        ...prev,
        [fieldPath]: true
      }));
    } else if (validationErrors[fieldPath]) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[fieldPath];
        return updated;
      });
    }
    
    return isValid;
  }, [formData, validationErrors]);

  /**
   * Validates a form step using validation rules
   * @param {Object} validationRules - Map of field paths to validation rules
   * @returns {Object} Validation result with errors and isValid flag
   */
  const validateStep = useCallback((validationRules) => {
    const errors = {};
    let isValid = true;
    const fieldsToTouch = {};
    
    // Check each validation rule
    Object.entries(validationRules).forEach(([fieldPath, rule]) => {
      const value = getNestedValue(formData, fieldPath);
      
      // Skip validation for optional empty fields
      if (rule.optional && (!value || (typeof value === 'string' && value.trim() === ''))) {
        return;
      }
      
      // Check required fields
      if (rule.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[fieldPath] = rule.message || 'This field is required';
        fieldsToTouch[fieldPath] = true;
        isValid = false;
        return;
      }
      
      // Run custom validator if provided
      if (rule.validator && value !== undefined) {
        const fieldIsValid = rule.validator(value, formData);
        if (!fieldIsValid) {
          errors[fieldPath] = rule.message || 'Invalid value';
          fieldsToTouch[fieldPath] = true;
          isValid = false;
        }
      }
    });
    
    // Update validation state
    setValidationErrors(errors);
    
    // Mark fields as touched if validation failed
    if (!isValid) {
      setTouchedFields(prev => ({
        ...prev,
        ...fieldsToTouch
      }));
    }
    
    return { isValid, errors };
  }, [formData]);

  /**
   * Resets form to initial values
   */
  const resetForm = useCallback(() => {
    setFormData(initialValues);
    setValidationErrors({});
    setTouchedFields({});
  }, [initialValues]);

  /**
   * Gets errors for a specific step
   * @param {Array|Object} fieldPaths - Array of field paths or object with field paths as keys
   * @returns {Object} Errors for the specified fields
   */
  const getErrorsForStep = useCallback((fieldPaths) => {
    if (Array.isArray(fieldPaths)) {
      return fieldPaths.reduce((stepErrors, path) => {
        if (validationErrors[path]) {
          stepErrors[path] = validationErrors[path];
        }
        return stepErrors;
      }, {});
    } else if (typeof fieldPaths === 'object') {
      return Object.keys(fieldPaths).reduce((stepErrors, path) => {
        if (validationErrors[path]) {
          stepErrors[path] = validationErrors[path];
        }
        return stepErrors;
      }, {});
    }
    return {};
  }, [validationErrors]);

  /**
   * Gets touched state for specific fields
   * @param {Array|Object} fieldPaths - Array of field paths or object with field paths as keys
   * @returns {Object} Touched state for the specified fields
   */
  const getTouchedForStep = useCallback((fieldPaths) => {
    if (Array.isArray(fieldPaths)) {
      return fieldPaths.reduce((stepTouched, path) => {
        stepTouched[path] = !!touchedFields[path];
        return stepTouched;
      }, {});
    } else if (typeof fieldPaths === 'object') {
      return Object.keys(fieldPaths).reduce((stepTouched, path) => {
        stepTouched[path] = !!touchedFields[path];
        return stepTouched;
      }, {});
    }
    return touchedFields;
  }, [touchedFields]);

  return {
    // State
    formData,
    validationErrors,
    touchedFields,
    
    // Form update functions
    setFormData,
    updateFormData,
    
    // Change handlers
    handleChange,
    handleNestedChange,
    handleDeepNestedChange,
    handleArrayChange,
    
    // Validation
    validateField,
    validateStep,
    
    // Utilities
    resetForm,
    getErrorsForStep,
    getTouchedForStep,
  };
};

export default useForm;